/**
 * DICOM Loader Manager — загрузка файлов, папок, DICOMDIR (Optimized)
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 */

import { cornerstone, dicomImageLoader } from '../core/init.js';
import { StudyQualityAnalyzer } from '../storage/studyQualityAnalyzer.js';
import { escapeHtml, showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { sortSlices } from './sliceSort.js';
import { patientsBadgeHTML } from '../ui/statusIcons.js';   // [11.1.6] qualityBadgeHTML убран вместе со значком качества // [7.0.48] SVG-статусы
import { forgetAllVolumes, volumeCount } from '../core/volumeRegistry.js';   // [7.0.60]
import { noteMemoryEvent } from '../ui/recoveryReport.js';                   // [7.0.60]
import { classifySop, classifyCine } from '../core/sopKind.js';              // [10.3.0] снимок или не снимок

// ── Lazy-init dicom-parser ──
let _dicomParser = null;
async function getDicomParser() {
  if (_dicomParser) return _dicomParser;
  try { _dicomParser = (await import('dicom-parser')).default || await import('dicom-parser'); } 
  catch(e) { log.debug('[Loader] import fallback:', e.message); }
  
  _dicomParser ??= dicomImageLoader?.external?.dicomParser ?? window.dicomParser;
  if (!_dicomParser) log.error('[LoaderManager] dicom-parser NOT FOUND!');
  return _dicomParser;
}

// ── Кэширование и State ──
const _seriesMap = new Map();
const _studyMap = new Map();
const _metaCache = new Map(); // Кэш метаданных для 3D MPR
const _dataSetCache = new Map(); // Кэш dataSet

// Оптимизированный LRU кэш (удаляем старые записи без создания промежуточных массивов)
export function cacheDataSet(imageId, dataSet) {
  _dataSetCache.set(imageId, dataSet);
  if (_dataSetCache.size > 250) { // у rich-оверлея есть запасной источник (naturalized), ОЗУ дороже
    let deleted = 0;
    for (const key of _dataSetCache.keys()) {
      _dataSetCache.delete(key);
      if (++deleted >= 50) break;
    }
  }
}

export function trimDataSetCache(keepIds) {
  if (!keepIds?.length) return;
  const keep = new Set(keepIds);
  for (const key of _dataSetCache.keys()) {
    if (!keep.has(key)) _dataSetCache.delete(key);
  }
}

/** Достать распарсенный dataSet по imageId (кадровый суффикс ?frame= отбрасывается) */
export function getCachedDataSet(imageId) {
  if (!imageId) return null;
  const base = imageId.split('?frame=')[0];
  return _dataSetCache.get(base) || _dataSetCache.get(imageId) || null;
}

// ── Провайдер метаданных Cornerstone (Критично для 3D) ──
let _metaProviderRegistered = false;
function _ensureMetaProvider() {
  if (_metaProviderRegistered || !cornerstone?.metaData) return;
  _metaProviderRegistered = true;
  // [7.0.57] Кадровый суффикс ?frame=N надо отбрасывать: метаданные кладутся по
  // БАЗОВОМУ imageId (строка с _metaCache.set), а в серию многокадрового файла
  // попадают идентификаторы вида `${imageId}?frame=1..N`. Провайдер искал их
  // как есть и для КАЖДОГО кадра возвращал undefined — многокадровые Enhanced
  // MR/CT оставались вовсе без геометрии (ни PixelSpacing, ни IPP/IOP, ни кадра
  // отсчёта), то есть измерения в миллиметрах по ним недостоверны.
  // Соседняя getCachedDataSet суффикс отбрасывает — здесь забыли.
  cornerstone.metaData.addProvider((type, imageId) => {
    const cached = _metaCache.get(imageId);
    if (cached) return cached[type];
    const base = String(imageId).split('?frame=')[0];
    return base !== imageId ? _metaCache.get(base)?.[type] : undefined;
  }, 20000);
}

// ── State загрузки и качества ──
let _loadingAborted = false;
let _qualityPatientsMap = null;
let _lastQualityAnalysis = null;

export const getQualityPatientsMap = () => _qualityPatientsMap;
export const getLastQualityAnalysis = () => _lastQualityAnalysis;
export const isAborted = () => _loadingAborted;

export function _setQualityState(patientsMap, analysis) {
  _qualityPatientsMap = patientsMap;
  _lastQualityAnalysis = analysis;
}

// ============================================================================
// UI ПРОГРЕССА ЗАГРУЗКИ (DOM API Optimization)
// ============================================================================

let _uiElements = null;

function initLoaderUI() {
  if (_uiElements) return _uiElements;

  const overlay = document.createElement('div');
  overlay.id = 'mikhmel-old-loading';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);backdrop-filter:blur(8px);z-index:999999;display:none;align-items:center;justify-content:center;flex-direction:column;';
  
  const inner = document.createElement('div');
  inner.style.cssText = 'background:linear-gradient(135deg,#0d0d24,#141432);padding:32px 40px;border-radius:16px;border:1px solid rgba(79,140,255,0.2);text-align:center;color:#fff;font-family:inherit;box-shadow:0 20px 60px rgba(0,0,0,0.6);width:380px;max-width:90vw;';
  
  const title = document.createElement('div');
  title.style.cssText = 'font-size:15px;font-weight:600;margin-bottom:16px;color:#dcdce8;';
  
  const barBg = document.createElement('div');
  barBg.style.cssText = 'width:100%;height:8px;background:rgba(255,255,255,0.08);border-radius:4px;overflow:hidden;display:none;position:relative;';
  
  const barFill = document.createElement('div');
  barFill.style.cssText = 'width:0%;height:100%;background:linear-gradient(90deg,#4f8cff,#00d4aa);transition:width 0.3s ease;border-radius:4px;position:relative;';
  
  // Shimmer effect on bar
  const shimmer = document.createElement('div');
  shimmer.style.cssText = 'position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent);animation:shimmer 1.5s infinite;';
  barFill.appendChild(shimmer);
  barBg.appendChild(barFill);

  const statsRow = document.createElement('div');
  statsRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-top:10px;font-size:11px;color:#8888aa;display:none;';
  
  const percText = document.createElement('span');
  percText.style.cssText = 'font-weight:700;color:#4f8cff;font-size:13px;';
  
  const sliceText = document.createElement('span');
  sliceText.style.cssText = 'font-size:11px;';
  
  const etaText = document.createElement('span');
  etaText.style.cssText = 'font-size:11px;';
  
  statsRow.append(percText, sliceText, etaText);

  const btn = document.createElement('button');
  btn.style.cssText = 'margin-top:20px;padding:10px 28px;background:rgba(255,68,102,0.15);border:1px solid rgba(255,68,102,0.3);color:#ff4466;border-radius:8px;cursor:pointer;font-size:12px;font-weight:600;display:none;transition:all 0.15s;';
  btn.textContent = '✕ Отмена';
  btn.onmouseenter = () => { btn.style.background = 'rgba(255,68,102,0.25)'; };
  btn.onmouseleave = () => { btn.style.background = 'rgba(255,68,102,0.15)'; };
  btn.onclick = abortLoading;

  // Inject shimmer animation
  if (!document.getElementById('loader-shimmer-css')) {
    const style = document.createElement('style');
    style.id = 'loader-shimmer-css';
    style.textContent = '@keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}';
    document.head.appendChild(style);
  }

  inner.append(title, barBg, statsRow, btn);
  overlay.appendChild(inner);
  document.body.appendChild(overlay);

  return _uiElements = { overlay, title, barBg, barFill, percText, btn, sliceText, etaText, statsRow, _startTime: 0 };
}

export function showLoading(msg, progress, loaded, total) {
  document.getElementById('loading-overlay')?.style.setProperty('display', 'none');
  const ui = initLoaderUI();
  
  ui.overlay.style.display = 'flex';
  ui.title.textContent = msg || 'Загрузка...';
  window._abortLoading = abortLoading;
  
  if (progress !== undefined) {
    const p = Math.min(100, Math.max(0, progress));
    ui.barBg.style.display = 'block';
    ui.statsRow.style.display = 'flex';
    ui.btn.style.display = 'inline-block';
    ui.barFill.style.width = `${p}%`;
    ui.percText.textContent = `${Math.round(p)}%`;
    
    // Slice counter
    if (loaded !== undefined && total !== undefined) {
      ui.sliceText.textContent = `${loaded} / ${total} срезов`;
    }
    
    // ETA calculation
    if (!ui._startTime || p < 1) ui._startTime = Date.now();
    if (p > 2 && ui._startTime) {
      const elapsed = (Date.now() - ui._startTime) / 1000;
      const rate = p / elapsed;
      const remaining = rate > 0 ? Math.ceil((100 - p) / rate) : 0;
      if (remaining > 0 && remaining < 600) {
        const min = Math.floor(remaining / 60);
        const sec = remaining % 60;
        ui.etaText.textContent = min > 0 ? `~${min}м ${sec}с` : `~${sec}с`;
      } else {
        ui.etaText.textContent = '';
      }
    }
  } else {
    ui.barBg.style.display = 'none';
    ui.statsRow.style.display = 'none';
    ui.btn.style.display = 'none';
  }
}

export function hideLoading() {
  if (_uiElements) { _uiElements.overlay.style.display = 'none'; _uiElements._startTime = 0; }
  _loadingAborted = false;
}

export function abortLoading() {
  _loadingAborted = true;
  hideLoading();
  log.warn('⚠️ Загрузка отменена пользователем');
}

// ============================================================================
// ПАРСИНГ И ЗАГРУЗКА ФАЙЛОВ (Batched Optimization)
// ============================================================================

export async function loadDICOMFiles(files, onProgress) {
  try {
    _ensureMetaProvider();
    _loadingAborted = false;
    
    const dp = await getDicomParser();
    if (!dp) throw new Error('dicom-parser not available');
  
    const total = files.length;
    let loaded = 0;
    const seriesGroups = {};
    // [10.3.0] Что отложили как «не снимок» — чтобы сказать вслух, а не молча.
    const _skippedNonImage = [];
    let _firstDataSet = null;
    const _dataSetsPerPatient = new Map();
  
    showLoading(`Анализ DICOM...`, 0, 0, total);
  
    // Обработка пачками (Batches) предотвращает зависание UI и краш памяти (OOM)
    const BATCH_SIZE = 50; 
    
    for (let i = 0; i < total; i += BATCH_SIZE) {
      if (_loadingAborted) break;
      const chunk = files.slice(i, i + BATCH_SIZE);
      
      await Promise.all(chunk.map(async (file) => {
        if (_loadingAborted) return;
        try {
          const imageId = dicomImageLoader.wadouri.fileManager.add(file);
          const arrayBuffer = await file.arrayBuffer();
          
          // Парсим только до пикселей (экономим память)
          const dataSet = dp.parseDicom(new Uint8Array(arrayBuffer), { untilTag: 'x7fe00010' });
          cacheDataSet(imageId, dataSet);
  
          if (!_firstDataSet) _firstDataSet = dataSet;
  
          // Извлечение базовых тегов
          const getStr = (tag, def = '') => dataSet.string(tag) || def;
          const seriesUID = getStr('x0020000e', 'unknown');
          const instanceNum = dataSet.intString('x00200013') || 0;
          const patientName = getStr('x00100010');
          const patId = getStr('x00100020');
          const sopInstanceUID = getStr('x00080018');      // SOP каждого среза — для ссылок RTSTRUCT
          const forUID = getStr('x00200052');              // настоящий Frame of Reference (0020,0052)

          const patKey = (patId || patientName || 'UNKNOWN').trim();
          if (!_dataSetsPerPatient.has(patKey)) {
            _dataSetsPerPatient.set(patKey, {
              name: patientName, id: patId,
              sex: getStr('x00100040'), birth: getStr('x00100030'), dataSet
            });
          }
          
          // Геометрия 3D MPR (Предрассчитываем один раз)
          // [10.19.1] Сырой тег храним отдельно: рабочему значению нужен
          // дефолт 1 (геометрия не терпит NaN), а протокол-контролю нужна
          // ПРАВДА — иначе серия с неизвестной толщиной проходит требование
          // «1 мм» как годная.
          const sliceThicknessRaw = dataSet.floatString('x00180050');
          const sliceThickness = sliceThicknessRaw || 1;
          const rows = dataSet.uint16('x00280010') || 0;
          const cols = dataSet.uint16('x00280011') || 0;

          // [10.3.0] Объекты БЕЗ пикселей в список серий не попадают.
          // Тот же рубеж, что и на главном пути (loaderWorkerBridge): в
          // ангиографическом исследовании рядом со съёмкой лежат Presentation
          // State — «состояние показа», пикселей в них нет вовсе. Правило — в
          // src/core/sopKind.js, файле без импортов, под тестом.
          const kind = classifySop({
            sopClassUID: getStr('x00080016'),
            modality: getStr('x00080060'),
            rows, columns: cols,
          });
          if (!kind.isImage) {
            _skippedNonImage.push(kind.kind);
            log.debug(`[Loader] не снимок, пропускаю: ${file.name} — ${kind.kind} (${kind.why})`);
            loaded++;
            return;
          }

          const ippStr = dataSet.string('x00200032');
          const iopStr = dataSet.string('x00200037');
          const psStr = dataSet.string('x00280030');
          
          const ipp = ippStr ? ippStr.split('\\').map(Number) : [0, 0, instanceNum * sliceThickness];
          const iop = iopStr ? iopStr.split('\\').map(Number) : [1, 0, 0, 0, 1, 0];
          const ps = psStr ? psStr.split('\\').map(Number) : [1, 1];
  
          // Запись в кэш провайдера метаданных
          _metaCache.set(imageId, {
            imagePlaneModule: {
              imagePositionPatient: ipp, imageOrientationPatient: iop,
              pixelSpacing: ps, rowPixelSpacing: ps[0], columnPixelSpacing: ps[1],
              sliceThickness, rows, columns: cols,
              frameOfReferenceUID: forUID || seriesUID   // реальный FOR, откат к seriesUID если тега нет
            },
            sopCommonModule: { sopInstanceUID, sopClassUID: getStr('x00080016') },
            generalImageModule: { sopInstanceUID },
            imagePixelModule: (() => {
              const ba = dataSet.uint16('x00280100') || 16;
              const bs = dataSet.uint16('x00280101') || ba;
              const hb = dataSet.uint16('x00280102') || (bs - 1);
              return {
                bitsAllocated: ba, bitsStored: bs, highBit: hb,
                pixelRepresentation: dataSet.uint16('x00280103') || 0,
                photometricInterpretation: getStr('x00280004', 'MONOCHROME2'),
                rows, columns: cols
              };
            })()
          });
  
          // Группировка по сериям
          if (!seriesGroups[seriesUID]) {
            seriesGroups[seriesUID] = {
              // [10.19.1] Заглушка 'unknown_study' — как в workerPool и
              // studyIndex: разные заглушки разводили серии без UID по разным
              // корзинам в зависимости от пути загрузки.
              seriesUID, studyUID: getStr('x0020000d', 'unknown_study'),
              modality: getStr('x00080060'), patientName, patientId: patId,
              frameOfReferenceUID: forUID || seriesUID,
              studyDesc: getStr('x00081030'), seriesDesc: getStr('x0008103e'),
              sopClassUID: getStr('x00080016'), images: []
            };
          }
  
          // [MR] Многокадровый файл (Enhanced MR и т.п.): раскрываем кадры в отдельные срезы.
          // Нумерация кадров у wadouri-загрузчика — с единицы (?frame=1).
          const nFrames = dataSet.intString('x00280008') || 1;
          const sliceLocation = dataSet.floatString('x00201041') || 0;

          if (nFrames > 1) {
            for (let f = 1; f <= nFrames; f++) {
              seriesGroups[seriesUID].images.push({
                imageId: `${imageId}?frame=${f}`,
                instanceNum: instanceNum * 100000 + f, // порядок: файл, затем кадр
                sliceLocation: 0,
                sliceThickness,
                sliceThicknessRaw,
                sopInstanceUID,                          // тот же SOP файла, кадр отдельно
                frameNumber: f,
                frameOfReferenceUID: forUID || seriesUID,
                _multiframe: true
              });
            }
          } else {
            seriesGroups[seriesUID].images.push({
              imageId, instanceNum,
              sliceLocation,
              sliceThickness,
              sliceThicknessRaw,
              ipp, iop,
              sopInstanceUID,                            // SOP среза — для ContourImageSequence
              frameOfReferenceUID: forUID || seriesUID
            });
          }
  
          loaded++;
        } catch (e) {
          // [10.3.0] String(e), а не e.message: dicom-parser бросает СТРОКУ,
          // у неё нет поля message — в журнале стояло «undefined».
          log.warn(`[Loader] пропускаю ${file.name}: ${e && e.message ? e.message : String(e)}`);
        }
      }));
  
      // Обновляем прогресс раз в батч, а не на каждый файл
      const percent = (loaded / total) * 100;
      showLoading(`Анализ DICOM...`, percent, loaded, total);
      if (onProgress) onProgress(loaded, total);
    }
  
    // Сортировка срезов в группах: по координате вдоль нормали (МРТ-надёжно), fallback — как раньше
    for (const group of Object.values(seriesGroups)) {
      sortSlices(group.images);

      // [10.3.0] Помечаем киносъёмку: у ангиографии кадры идут во времени, а
      // не по телу, и объём из них не строится. Подробности — в sopKind.js.
      const zs = group.images.map(i => (Array.isArray(i.ipp) ? i.ipp[2] : null)).filter(Number.isFinite);
      const spread = zs.length >= 2 ? Math.max(...zs) - Math.min(...zs) : null;
      const cine = classifyCine({
        modality: group.modality, numberOfFrames: group.images.length, spatialSpreadMm: spread,
      });
      group.isCine = cine.isCine;
      group.cineWhy = cine.why;
      if (cine.isCine) log.info(`[Loader] серия «${group.seriesDesc || group.modality}» — киносъёмка: ${cine.why}`);

      _seriesMap.set(group.seriesUID, group);
    }

    if (_skippedNonImage.length) {
      const byKind = {};
      for (const k of _skippedNonImage) byKind[k] = (byKind[k] || 0) + 1;
      log.info('[Loader] не снимки, в список серий не попали: '
        + Object.entries(byKind).map(([k, n]) => `${k} — ${n}`).join('; '));
    }
  
    if (!_loadingAborted) hideLoading();
    processQualityAnalysis(_firstDataSet, _dataSetsPerPatient);
  
    return seriesGroups;
  } catch (e) { log.warn(`[loaderManager] loadDICOMFiles failed:`, e.message); throw e; }
}

export async function loadDICOMFolder(fileList, onProgress) {
  try {
    const files = Array.from(fileList).filter((f) => !f.name.startsWith('.') && f.size > 128);
    return loadDICOMFiles(files, onProgress);
  } catch (e) { log.warn(`[loaderManager] loadDICOMFolder failed:`, e.message); return null; }
}

// ============================================================================
// АНАЛИЗ КАЧЕСТВА (Вынесен из загрузчика для чистоты)
// ============================================================================

function processQualityAnalysis(firstDataSet, dataSetsPerPatient) {
  if (!firstDataSet) return;
  try {
    const patientsMap = {};
    for (const [key, pat] of dataSetsPerPatient) {
      const qParams = StudyQualityAnalyzer.extract(pat.dataSet);
      patientsMap[key] = {
        name: pat.name, id: pat.id, sex: pat.sex, birth: pat.birth,
        analyses: [StudyQualityAnalyzer.analyze(qParams)]
      };
    }

    const keys = Object.keys(patientsMap);
    const isMulti = keys.length > 1;
    _setQualityState(patientsMap, isMulti ? patientsMap : patientsMap[keys[0]]?.analyses[0]);

    // [9.0.0] На телефоне отчёт о качестве не показываем.
    // Он занимает место в узкой строке состояния, а решение по нему всё равно
    // принимают за компьютером. Сам анализ при этом выполняется и хранится:
    // если исследование потом открыть на десктопе, всё будет на месте.
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // Обновление UI-бейджа
    const statusEl = isMobile
      ? null
      : (document.getElementById('status-bar') || document.getElementById('dicom-study-info'));
    if (isMobile) document.getElementById('quality-badge')?.remove();
    if (statusEl) {
      document.getElementById('quality-badge')?.remove();
      // [7.0.48] Счётчики-статусы рендерятся инлайновым SVG (см. statusIcons.js)
      // [11.1.6] ЗНАЧОК КАЧЕСТВА УБРАН — по решению владельца.
      //
      // Он показывал цветной кружок и два числа: «🟡 4 ✓ 3 ✕» — уровень шума,
      // сколько задач исследование позволяет и сколько нет. Владелец спросил
      // «что это за кнопочки, что они делают» — и это исчерпывающая оценка их
      // понятности: три значка с числами без единой подписи. Вход в анализ
      // теперь есть кнопкой «Качество КТ» в верхней панели (11.1.3), где у
      // него хотя бы имя.
      //
      // ОСТАВЛЕН счётчик пациентов: он не про качество, а про безопасность —
      // предупреждает, что в папке лежат РАЗНЫЕ БОЛЬНЫЕ. Другого такого
      // предупреждения в программе нет (проверено поиском), и снять его
      // заодно значило бы убрать единственный сигнал о смешанной папке.
      if (isMulti) {
        const qSpan = document.createElement('span');
        qSpan.id = 'quality-badge';
        qSpan.style.cssText = 'cursor:pointer;margin-left:8px;';
        qSpan.title = 'В загруженной папке несколько разных пациентов. Щёлкните, чтобы посмотреть список';
        qSpan.innerHTML = ` ${patientsBadgeHTML(keys.length)}`;
        qSpan.onclick = showStudyQuality;
        statusEl.appendChild(qSpan);
      }
    }
    
    // [11.1.4] АВТОПОКАЗ ПЛАШКИ КАЧЕСТВА УБРАН — по решению владельца.
    //
    // Здесь при каждой загрузке всплывало окно «Quality Gate — N
    // предупреждений» (а при чистом исследовании — плашка «качество хорошее»).
    // Врач открывает исследование, чтобы смотреть снимок, и получает поверх
    // него сообщение, которого не просил. При просмотре десятка исследований
    // подряд это десять закрываний.
    //
    // Разбор никуда не делся и по-прежнему считается при загрузке: значок
    // качества в строке состояния остаётся, а полный вердикт открывается
    // кнопкой «Качество КТ» в верхней панели (11.1.3) — то есть тогда, когда
    // врач сам захотел его увидеть.
    //
    // Функция _showQualityGateNotification оставлена: её зовёт кнопка, если
    // понадобится краткая сводка. Убран только автоматический вызов.

    // [П.8] Автопоказ панели убран: сведения о качестве — по клику на бейдж в топбаре
  } catch (e) { log.warn('[Quality] Analysis failed:', e.message); }
}

function _showQualityGateNotification(patientsMap, keys, isMulti) {
  try {
    const warnings = [];
    
    for (const key of keys) {
      const analysis = patientsMap[key]?.analyses?.[0];
      if (!analysis) continue;
      
      // Высокий шум
      if (analysis.noiseLevel === 'high') {
        warnings.push('⚠️ Высокий уровень шума');
      }
      
      // Ограничения (массив объектов {icon, feature, detail})
      if (analysis.limitations?.length > 0) {
        for (const lim of analysis.limitations.slice(0, 3)) {
          warnings.push(`${lim.icon || '⚠️'} ${lim.feature}: ${lim.detail}`);
        }
      }
      
      // Толстые срезы (>5mm для CT)
      const st = analysis.params?.sliceThickness ?? analysis.params?.sliceSpacing;
      if (st && st > 5) {
        warnings.push(`⚠️ Толстые срезы (${st.toFixed?.(1) || st} мм) — 3D-реконструкция может быть неточной`);
      }
      
      // Малое количество срезов
      const sliceCount = analysis.params?.numberOfFrames;
      if (sliceCount && sliceCount < 20) {
        warnings.push(`⚠️ Мало срезов (${sliceCount}) — MPR-реконструкция ограничена`);
      }
    }
    
    if (warnings.length === 0) {
      showToast('✅ Качество исследования: хорошее', 'success', 3000);
      return;
    }
    
    // Показываем плавающее уведомление
    const existing = document.getElementById('quality-gate-toast');
    if (existing) existing.remove();
    
    const toast = document.createElement('div');
    toast.id = 'quality-gate-toast';
    toast.style.cssText = `
      position: fixed; bottom: 20px; right: 20px; z-index: 30000;
      background: linear-gradient(135deg, #1a1a2e, #16213e); 
      border: 1px solid rgba(255,180,0,0.3); border-radius: 12px;
      padding: 16px 20px; max-width: 380px; color: #eee;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5); backdrop-filter: blur(12px);
      font-size: 12px; line-height: 1.6;
      animation: slideInRight 0.3s ease-out;
    `;
    
    const header = document.createElement('div');
    header.style.cssText = 'font-weight:600;font-size:13px;margin-bottom:8px;color:#ffb400;';
    header.textContent = `🔍 Quality Gate — ${warnings.length} предупреждений`;
    
    const body = document.createElement('div');
    body.style.cssText = 'color:#aab;';
    body.innerHTML = warnings.map(w => `<div style="margin:3px 0">${escapeHtml(w)}</div>`).join('');
    
    const closeBtn = document.createElement('button');
    closeBtn.style.cssText = 'position:absolute;top:8px;right:10px;background:none;border:none;color:#666;cursor:pointer;font-size:16px;';
    closeBtn.textContent = '✕';
    closeBtn.onclick = () => toast.remove();
    
    const detailBtn = document.createElement('button');
    detailBtn.style.cssText = 'margin-top:10px;padding:6px 16px;background:rgba(79,140,255,0.15);border:1px solid rgba(79,140,255,0.3);color:#4f8cff;border-radius:6px;cursor:pointer;font-size:11px;';
    detailBtn.textContent = 'Подробный анализ';
    detailBtn.onclick = () => { toast.remove(); showStudyQuality(); };
    
    toast.append(closeBtn, header, body, detailBtn);
    document.body.appendChild(toast);
    
    // Inject animation if needed
    if (!document.getElementById('qgate-css')) {
      const s = document.createElement('style');
      s.id = 'qgate-css';
      s.textContent = '@keyframes slideInRight{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}';
      document.head.appendChild(s);
    }
    
    // Авто-скрытие через 15с
    setTimeout(() => toast.remove(), 15000);
  } catch (e) { log.warn('[QualityGate] Notification failed:', e.message); }
}

// ============================================================================
// УТИЛИТЫ И WADO
// ============================================================================

export function getSeriesImageIds(seriesUID, step = 1) {
  const series = _seriesMap.get(seriesUID);
  if (!series) return [];

  let images = series.images;
  if (step > 1) {
    images = images.filter((_, i) => i % step === 0);
    images.forEach(img => {
      const meta = _metaCache.get(img.imageId);
      if (meta?.imagePlaneModule) meta.imagePlaneModule.sliceThickness = (img.sliceThickness || 1) * step;
    });
  }
  return images.map(img => img.imageId);
}

export async function loadWADORS(wadoRsRoot, studyUID, seriesUID) {
  try {
    const res = await fetch(`${wadoRsRoot}/studies/${studyUID}/series/${seriesUID}/metadata`, { headers: { Accept: 'application/dicom+json' }});
    return (await res.json()).map(inst => `wadors:${wadoRsRoot}/studies/${studyUID}/series/${seriesUID}/instances/${inst['00080018']?.Value?.[0]}/frames/1`);
  } catch (e) { log.warn(`[loaderManager] loadWADORS failed:`, e.message); return null; }
}

export function loadWADOURI(baseUrl, objectUIDs) {
  return objectUIDs.map(uid => `wadouri:${baseUrl}?requestType=WADO&studyUID=${uid.studyUID}&seriesUID=${uid.seriesUID}&objectUID=${uid.sopUID}&contentType=application%2Fdicom`);
}

export const getAllSeries = () => Array.from(_seriesMap.values());
export const getSeriesMetadata = (uid) => _seriesMap.get(uid) || null;
export const filesToImageIds = (files) => files.map(f => dicomImageLoader.wadouri.fileManager.add(f));

export function clearAllData() {
  // [7.0.60] Здесь очистка кэша уместна — исследование закрывают осознанно.
  // Но реестр томов надо обнулить вместе с ним: иначе восстановление после
  // возврата из фона попыталось бы вернуть тома только что закрытой серии.
  try { noteMemoryEvent('закрытие исследования', 'очистил весь кэш', `томов в реестре было: ${volumeCount()}`); } catch (e) { /* */ }
  forgetAllVolumes();
  cornerstone.cache?.purgeCache();
  _seriesMap.clear(); _studyMap.clear(); _metaCache.clear(); _dataSetCache.clear();
  _setQualityState(null, null);
  
  import('../loaders/loaderWorkerBridge.js')
    .then(m => m.clearWorkerData?.())
    .catch(e => log.debug('[Loader]', e.message));
}

// [6.6.8] Словарь адрес→человекочитаемое имя для распространённых DICOM-тегов.
// Покрывает то, что реально встречается в МР-исследованиях головы (GE и др.).
const DICOM_TAG_DICT = {
  '00080005': 'Specific Character Set', '00080008': 'Image Type', '00080016': 'SOP Class UID',
  '00080018': 'SOP Instance UID', '00080020': 'Study Date', '00080021': 'Series Date',
  '00080022': 'Acquisition Date', '00080023': 'Content Date', '00080030': 'Study Time',
  '00080031': 'Series Time', '00080032': 'Acquisition Time', '00080033': 'Content Time',
  '00080050': 'Accession Number', '00080060': 'Modality', '00080070': 'Manufacturer',
  '00080080': 'Institution Name', '00080090': 'Referring Physician', '00081010': 'Station Name',
  '00081030': 'Study Description', '0008103e': 'Series Description', '00081090': 'Manufacturer Model',
  '00100010': 'Patient Name', '00100020': 'Patient ID', '00100030': 'Patient Birth Date',
  '00100040': 'Patient Sex', '00101010': 'Patient Age', '00101020': 'Patient Size', '00101030': 'Patient Weight',
  '00180015': 'Body Part Examined', '00180020': 'Scanning Sequence', '00180021': 'Sequence Variant',
  '00180022': 'Scan Options', '00180023': 'MR Acquisition Type', '00180024': 'Sequence Name',
  '00180025': 'Angio Flag', '00180050': 'Slice Thickness', '00180080': 'Repetition Time (TR)',
  '00180081': 'Echo Time (TE)', '00180082': 'Inversion Time (TI)', '00180083': 'Number of Averages',
  '00180084': 'Imaging Frequency', '00180085': 'Imaged Nucleus', '00180087': 'Magnetic Field Strength',
  '00180088': 'Spacing Between Slices', '00180091': 'Echo Train Length', '00180093': 'Percent Sampling',
  '00180094': 'Percent Phase FOV', '00180095': 'Pixel Bandwidth', '00181000': 'Device Serial Number',
  '00181020': 'Software Versions', '00181030': 'Protocol Name', '00181250': 'Receive Coil Name',
  '00181310': 'Acquisition Matrix', '00181312': 'Phase Encoding Direction', '00181314': 'Flip Angle',
  '00185100': 'Patient Position', '0020000d': 'Study Instance UID', '0020000e': 'Series Instance UID',
  '00200010': 'Study ID', '00200011': 'Series Number', '00200012': 'Acquisition Number',
  '00200013': 'Instance Number', '00200032': 'Image Position (Patient)', '00200037': 'Image Orientation',
  '00200052': 'Frame of Reference UID', '00201041': 'Slice Location', '00280002': 'Samples Per Pixel',
  '00280004': 'Photometric Interpretation', '00280010': 'Rows', '00280011': 'Columns',
  '00280030': 'Pixel Spacing', '00280100': 'Bits Allocated', '00280101': 'Bits Stored',
  '00280102': 'High Bit', '00280103': 'Pixel Representation', '00281050': 'Window Center',
  '00281051': 'Window Width', '00281052': 'Rescale Intercept', '00281053': 'Rescale Slope',
  '7fe00010': 'Pixel Data',
};

export async function getDICOMTags(imageId) {
  const tags = {};
  if (!imageId) return tags;
  // [6.3.1] Многокадровые файлы: в кэше лежит базовый ключ без ?frame=N
  const baseId = imageId.split('?frame=')[0];
  let dataSet = _dataSetCache.get(baseId) || _dataSetCache.get(imageId);

  if (!dataSet) {
    // [6.6.8] Надёжная догрузка сырого DICOM через wadouri.dataSetCacheManager —
    // возвращает распарсенный dataSet со ВСЕМИ тегами (адреса + VR), в отличие
    // от imageLoader, который для MPR-объёма dataSet не отдаёт.
    try {
      const wadouriId = baseId.startsWith('wadouri:') ? baseId : ('wadouri:' + baseId);
      const dscm = dicomImageLoader?.wadouri?.dataSetCacheManager;
      if (dscm?.load) {
        const loaded = await dscm.load(wadouriId, dicomImageLoader.wadouri.loadFileRequest || undefined);
        if (loaded?.elements) { dataSet = loaded; cacheDataSet(baseId, dataSet); }
      }
    } catch (e) { log.debug('[Loader] dataSetCacheManager load failed:', e.message); }
  }

  if (!dataSet) {
    // Кэш подрезается ради памяти — догружаем кадр заново (запасной путь)
    try {
      const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
      dataSet = image?.data;
      if (dataSet) cacheDataSet(baseId, dataSet);
    } catch (e) { log.debug('[Loader] getDICOMTags fallback failed:', e.message); }
  }

  if (dataSet?.elements) {
    for (const tag of Object.keys(dataSet.elements)) {
      const el = dataSet.elements[tag];
      let value = '';
      try { value = dataSet.string(tag) || ''; } catch (e) { value = ''; }
      // tag вида 'x00180080' → имя из словаря DICOM (если известно)
      const addr = tag.replace(/^x/, '').toLowerCase();
      tags[tag] = { tag, vr: el.vr || '', value, length: el.length, name: DICOM_TAG_DICT[addr] || undefined };
    }
    return tags;
  }

  // [6.3.1] Последний фолбэк — натурализованные метаданные Cornerstone
  // (наш провайдер отдаёт instance/imagePlane/generalSeries), если dataSet недоступен.
  // [6.3.2] Ключи-слова маппим обратно на реальные DICOM-адреса и имена,
  // чтобы окно группировало и подписывало теги как при чтении сырого файла.
  const KW2TAG = {
    patientName: ['x00100010', 'Patient Name'], patientId: ['x00100020', 'Patient ID'],
    patientBirthDate: ['x00100030', 'Birth Date'], patientSex: ['x00100040', 'Sex'],
    patientAge: ['x00101010', 'Age'], patientWeight: ['x00101030', 'Weight'],
    studyDate: ['x00080020', 'Study Date'], studyTime: ['x00080030', 'Study Time'],
    studyDescription: ['x00081030', 'Study Description'], accessionNumber: ['x00080050', 'Accession'],
    studyInstanceUID: ['x0020000d', 'Study UID'], studyId: ['x00200010', 'Study ID'],
    seriesDescription: ['x0008103e', 'Series Description'], seriesNumber: ['x00200011', 'Series Number'],
    seriesDate: ['x00080021', 'Series Date'], seriesTime: ['x00080031', 'Series Time'],
    seriesInstanceUID: ['x0020000e', 'Series UID'], modality: ['x00080060', 'Modality'],
    bodyPartExamined: ['x00180015', 'Body Part'], sliceThickness: ['x00180050', 'Slice Thickness'],
    spacingBetweenSlices: ['x00180088', 'Spacing Between Slices'], repetitionTime: ['x00180080', 'TR'],
    echoTime: ['x00180081', 'TE'], inversionTime: ['x00180082', 'TI'], flipAngle: ['x00181314', 'Flip Angle'],
    echoTrainLength: ['x00180091', 'Echo Train'], magneticFieldStrength: ['x00180087', 'Field Strength'],
    rows: ['x00280010', 'Rows'], columns: ['x00280011', 'Columns'], pixelSpacing: ['x00280030', 'Pixel Spacing'],
    bitsAllocated: ['x00280100', 'Bits Allocated'], bitsStored: ['x00280101', 'Bits Stored'],
    windowCenter: ['x00281050', 'Window Center'], windowWidth: ['x00281051', 'Window Width'],
    instanceNumber: ['x00200013', 'Instance Number'], sliceLocation: ['x00201041', 'Slice Location'],
    imagePositionPatient: ['x00200032', 'Image Position'], imageOrientationPatient: ['x00200037', 'Image Orientation'],
    manufacturer: ['x00080070', 'Manufacturer'], institutionName: ['x00080080', 'Institution'],
    stationName: ['x00081010', 'Station Name'], manufacturerModelName: ['x00081090', 'Model'],
    contrastBolusAgent: ['x00180010', 'Contrast Agent'], patientPosition: ['x00185100', 'Patient Position'],
    softwareVersions: ['x00181020', 'Software'], receiveCoilName: ['x00181250', 'Coil'],
  };
  try {
    const mods = ['instance', 'generalSeriesModule', 'generalStudyModule', 'patientModule', 'imagePlaneModule', 'generalImageModule'];
    const seen = new Set();
    const seenNorm = new Set(); // нормализованные имена — чтобы схлопнуть регистровые дубликаты
    for (const m of mods) {
      const md = cornerstone.metaData.get(m, imageId);
      if (md && typeof md === 'object') {
        for (const [k, v] of Object.entries(md)) {
          if (v == null || typeof v === 'object') continue;
          const mapped = KW2TAG[k];
          const tagKey = mapped ? mapped[0] : ('meta_' + k);
          // Дубликат по адресу
          if (seen.has(tagKey)) continue;
          // Дубликат по нормализованному имени (acquisitionDate == AcquisitionDate)
          const norm = k.toLowerCase();
          if (!mapped && seenNorm.has(norm)) continue;
          // Если этот тег уже пришёл из основного dataSet-набора — не дублируем
          if (mapped && tags[mapped[0]]) continue;
          seen.add(tagKey);
          seenNorm.add(norm);
          tags[tagKey] = { tag: tagKey, vr: '', value: String(v), length: 0, name: mapped ? mapped[1] : k };
        }
      }
    }
  } catch (e) { log.debug('[Loader] getDICOMTags meta fallback:', e.message); }
  return tags;
}

// ============================================================================
// ПАНЕЛЬ АНАЛИЗА КАЧЕСТВА (Clean DOM injection)
// ============================================================================

export function showStudyQuality() {
  const data = _qualityPatientsMap || _lastQualityAnalysis;
  if (!data) {
    // [11.1.3] Раньше здесь стоял только log.warn — невидимый при обычном
    // уровне консоли. Пока окно открывалось значком в строке состояния, это
    // сходило: значка без данных попросту не было. Теперь у функции есть
    // КНОПКА «Качество КТ» в верхней панели, а кнопка видна всегда — и
    // молчание в ответ на нажатие неотличимо от поломки. Тот же урок, что со
    // связкой окон: «исполняется» и «работает» — разные вещи.
    log.warn('Сначала загрузите DICOM исследование');
    showToast('Анализировать нечего: сначала откройте исследование', 'info', 3000);
    return;
  }

  const existing = document.getElementById('qualityPanel');
  if (existing) { existing.remove(); return; }

  const panel = document.createElement('div');
  panel.id = 'qualityPanel';
  panel.style.cssText = 'position:fixed;right:10px;top:60px;width:440px;max-width:calc(100vw - 20px);max-height:80vh;overflow-y:auto;z-index:10000;background:#12121e;border:1px solid #2a2a40;border-radius:10px;padding:16px;box-shadow:0 8px 32px rgba(0,0,0,0.6);'
    + (window.innerWidth <= 768 ? 'right:0;top:0;width:100vw;max-width:100vw;height:100vh;max-height:100vh;border-radius:0;padding:12px;' : '');

  const isMulti = typeof data === 'object' && !data.isCT && !data.params;
  const html = isMulti ? StudyQualityAnalyzer.renderMultiPatientHTML(data) : StudyQualityAnalyzer.renderHTML(data);

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '&times;';
  closeBtn.style.cssText = window.innerWidth <= 768
    ? 'position:sticky;top:0;right:0;float:right;background:#12121e;border:none;color:#fff;font-size:28px;cursor:pointer;line-height:1;padding:8px 12px;z-index:1;'
    : 'float:right;background:none;border:none;color:#888;font-size:20px;cursor:pointer;line-height:1;';
  closeBtn.onclick = () => panel.remove();

  panel.appendChild(closeBtn);
  panel.insertAdjacentHTML('beforeend', html);
  document.body.appendChild(panel);

  // Делегирование событий для табов (надежнее, чем навешивать на каждый элемент)
  panel.addEventListener('click', (e) => {
    const tab = e.target.closest('.sq-mp-tab');
    if (!tab) return;
    
    panel.style.opacity = '1';
    panel.querySelectorAll('.sq-mp-tab').forEach(t => {
      t.classList.remove('sq-mp-active');
      Object.assign(t.style, { background: 'transparent', color: '#888', borderBottomColor: 'transparent' });
    });
    
    tab.classList.add('sq-mp-active');
    Object.assign(tab.style, { background: 'rgba(255,255,255,0.08)' });
    
    panel.querySelectorAll('.sq-mp-panel').forEach(p => {
      p.style.display = p.dataset.sqPanel === tab.dataset.sqTab ? 'block' : 'none';
    });
  });
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Load stats, retry, memory-safe batch
// ══════════════════════════════════════════════════════════════

const _loadStats = { totalLoads: 0, totalFiles: 0, totalErrors: 0, totalTimeSec: 0, lastLoadTimeSec: 0 };

/**
 * Get cumulative load statistics
 * @returns {Object}
 */
export function getLoadStats() { return { ..._loadStats }; }

/**
 * Get all loaded series with image counts
 * @returns {Array<{uid:string, modality:string, desc:string, images:number}>}
 */
export function getSeriesSummary() {
  return getAllSeries().map(s => ({
    uid: s.seriesUID?.substring(0, 24) + '...',
    modality: s.modality || '?',
    desc: s.seriesDesc || s.studyDesc || '',
    images: (s.images || s.files || []).length,
    patient: s.patientName || '',
  }));
}

/**
 * Get DICOM metadata cache size
 * @returns {{cachedDataSets:number, estimatedMB:number}}
 */
export function getDataSetCacheStats() {
  // _dataSetCache is internal but we can estimate
  let count = 0;
  try { if (window._mikhmelDataSetCache) count = window._mikhmelDataSetCache.size || 0; } catch (e) { log.debug("[loaderManager]", e.message); }
  return { cachedDataSets: count, estimatedMB: +(count * 0.05).toFixed(1) };
}

/**
 * Reload a specific series (re-parse from files)
 * @param {string} seriesUID
 * @returns {Promise<string[]>} imageIds
 */
export async function reloadSeries(seriesUID) {
  try {
    const series = getSeriesMetadata(seriesUID);
    if (!series) { log.warn('[Loader] Series not found:', seriesUID); return []; }
    log.debug(`[Loader] Reloading series ${seriesUID}...`);
    return getSeriesImageIds(seriesUID);
  } catch (e) { log.warn(`[loaderManager] reloadSeries failed:`, e.message); return []; }
}

/**
 * Get total memory footprint estimate of all loaded DICOM data
 * @returns {{seriesCount:number, totalImages:number, estimatedMB:number}}
 */
export function getLoaderMemoryEstimate() {
  const all = getAllSeries();
  let totalImages = 0;
  for (const s of all) totalImages += (s.images || s.files || []).length;
  // ~0.5MB per slice average (512x512 16bit)
  return { seriesCount: all.length, totalImages, estimatedMB: +(totalImages * 0.5).toFixed(0) };
}
